import { z } from 'zod';
/**
 * Ce qu'une lecture de document fournisseur rend au formulaire de devis.
 *
 * Les montants sont en euros, comme partout dans le contrat, et valent `0`
 * quand le document ne les porte pas — un hébergement à 0 se voit dans le
 * formulaire et se corrige, là où un prix inventé passerait inaperçu.
 *
 * Les dates sont des chaînes ISO (`AAAA-MM-JJ`), vides si le document n'en
 * donne pas. Pas de `z.coerce.date()` ici : la valeur traverse JSON et c'est le
 * formulaire qui la convertit, au moment où il fabrique le bloc.
 */
export const extractedHotelSchema = z.object({
    name: z.string(),
    fromDate: z.string(),
    location: z.string(),
    numberOfStars: z.number(),
    price: z.number(),
    roomType: z.string(),
    toDate: z.string(),
    /** Navettes desservant cet hébergement, regroupées en une ligne. */
    transferDescription: z.string(),
    transferPrice: z.number(),
});
export const extractedCarRentalSchema = z.object({
    details: z.string(),
    fromDate: z.string(),
    location: z.string(),
    price: z.number(),
    specifications: z.string(),
    toDate: z.string(),
});
/**
 * `netAmount` porte le montant lu : un devis fournisseur donne un coût, pas un
 * prix de vente. `price` reste à l'agent — voir la spec, règle des prix.
 */
export const extractedExtraSchema = z.object({
    name: z.string(),
    providerName: z.string(),
    description: z.string(),
    netAmount: z.number(),
    providerExternalRef: z.string(),
});
export const documentExtractionResultSchema = z.object({
    carRentals: z.array(extractedCarRentalSchema),
    /**
     * Code ISO lu dans le document. Les montants sont exprimés DANS cette devise :
     * c'est au formulaire de les convertir, une fois le taux confirmé par l'agent.
     */
    currency: z.string(),
    /**
     * Taux indicatif « 1 EUR = N <devise> », proposé quand le document n'est pas
     * en euros. Null quand la devise est inconnue de la source, qu'elle ne répond
     * pas, ou que le document est déjà en euros. C'est une proposition : l'agent
     * la corrige avant qu'elle s'applique.
     */
    exchangeRate: z
        .object({ currency: z.string(), date: z.string(), rate: z.number(), source: z.string() })
        .nullable(),
    extras: z.array(extractedExtraSchema),
    hotels: z.array(extractedHotelSchema),
    /**
     * Ce que le prix comprend et ne comprend pas, mis en forme pour le client :
     * « Compris : » puis « Non compris : », une ligne par poste. Vide si le
     * document n'en dit rien. Le formulaire l'ajoute aux notes du devis, qui
     * s'impriment en « Informations complémentaires ». Ce ne sont pas des
     * prestations : une carte « Visas » dirait au client qu'il l'a.
     */
    inclusions: z.string(),
    /**
     * Doutes rattachés à un champ précis — « hotels[0].price », « packagePrice ».
     * Le formulaire les signale sur le champ concerné plutôt qu'en bloc.
     *
     * Ce sont des constats du serveur, jamais une confiance déclarée par le
     * modèle : mesuré le 14/09/2026, celui-ci annonçait 0,93 à 0,99 de certitude
     * sur des lignes dont il ignorait le prix, et 0,99 à 1 sur un devis
     * entièrement chiffré. Les deux plages se recouvrent : rien ne serait jamais
     * signalé.
     */
    notices: z.array(z.object({ field: z.string(), reason: z.string() })),
    /**
     * Prix forfaitaire lu dans le document, en euros. Null quand le fournisseur
     * détaille ses lignes, ce qui reste le cas courant.
     */
    packagePrice: z.number().nullable(),
    /**
     * Ce que l'agent doit savoir et qui ne vise aucun champ : devise étrangère,
     * document dont rien n'a été tiré.
     */
    warnings: z.array(z.string()),
});
